import { useTranslation } from "react-i18next";
import { DataGrid } from "../ui";
import { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, FormControl, Grid, InputLabel, Link, TextField, Typography } from "@mui/material";
import SettingsIcon from '@mui/icons-material/Settings';
import { deleteIcon } from "../assets";
import { GLOBAL_ACTION } from "../types/global.enum";
import { useFormik } from "formik";
import { deleteAppConfig, getAppConfig, updateAppConfig } from "../services/config.service";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { getUserRoles, ROLES } from "../services/user.service";
import { configurationSchema } from '../helpers/validationSchemas.helper';
import { globalNotification, toggleAlert, toggleSpinner } from '../store/resuders/app.reducer';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useAppDispatch } from '../store/hooks';


interface IAppConfig {
    configName:string;
    configValue:string;
    id:Number;
    superConfig:boolean;
    fixedConfig:boolean
};

export const Configuration = ()=> {
    const { t } = useTranslation();
     const roles = getUserRoles() as ROLES[];
    const [data, setData] = useState<IAppConfig[]>([]);
    const [action, setAction] = useState<GLOBAL_ACTION>(GLOBAL_ACTION.NONE);
    const [selectedIds, setSelectedIds] = useState<any>([]);
    const [openReminder, setOpenReminder] = useState('');
    const dispatch = useAppDispatch();

    const selectionChanged = (id: any) => {
        setSelectedIds(id);
      };

    const deleteSelected = async () => {
        if (selectedIds?.length) {
           const ids = data.filter(d=>selectedIds.includes(d.id) && !d.fixedConfig)?.map(d=>d.id);
           dispatch(toggleSpinner(true));
           const result : any = await deleteAppConfig(ids);
           await loadData();

           dispatch(toggleSpinner(false));
            if (result && typeof result !== 'boolean' && ["ERR_BAD_RESPONSE", "ERR_BAD_REQUEST"].includes(result.code)) {
                dispatch(toggleAlert(result.response?.data?.data?.messages?.join(',') || 'An error occurred'));
            } else {
                dispatch(globalNotification('The selected configurations have been deleted.'));
            }
        }
    };

    const edit = (data: any) => () => {
        formik.setValues(data);
        setAction(GLOBAL_ACTION.EDIT);

      };

    const columns: any[] = [
        {
            caption: t("ID"),
            width: "5%",
            fieldName: "id",
            sortable: true,
            
          },
        {
          caption: t("Configuration Name"),
          width: "30%",
          fieldName: "configName",
          sortable: true,
          redner: (params: any) => (
            <div className="flex-center">
              <Link onClick={edit(params)} className="a-link-black">
                {params.configName}
              </Link>
            </div>
          ),
        },
        { caption: t("Configuration Value"), width: "30%", fieldName: "configValue", sortable: true, },
        { caption: t("Description"), width: "35%", fieldName: "description", sortable: true, },
       
      ];

      const formik = useFormik({
        validateOnChange: false,
        validateOnBlur: false,
        initialValues: {
          id: null,
          configName: '',
          configValue: '',
          description: '',
          fixedConfig: false,
        },
        validationSchema: configurationSchema,
        onSubmit: async (values) => {
          dispatch(toggleSpinner(false));
          const result: any = await updateAppConfig(values);
          await loadData();
          
          dispatch(toggleSpinner(false));
          if (result && typeof result !== 'boolean' && ["ERR_BAD_RESPONSE", "ERR_BAD_REQUEST"].includes(result.code)) {
              dispatch(toggleAlert(result.response?.data?.data?.messages?.join(',') || 'An error occurred'));
          } else {
              dispatch(globalNotification(values.id ? 'The configuration is updated.' : 'The new configuration is created.'));
              if (!values.id) {
                formik.resetForm();
            }
          }
        },
      });

      const updateAction = () => {
        switch (action) {
          case GLOBAL_ACTION.NONE: {
            setAction(GLOBAL_ACTION.ADD);
            formik.resetForm();
            break;
          }
    
          default: {
            setAction(GLOBAL_ACTION.NONE);
            
          }
        }
      };

      const loadData = async()=> {
         const appConfig:IAppConfig[] = await getAppConfig();
         
         if(roles.includes(ROLES.SUPER_ADMIN)) {
            setData(appConfig.filter((c:any)=> c.superConfig));
         } else {
            setData(appConfig.filter((c:any)=> !c.superConfig));
         }
      }

      const handleSave = async () => {
        await formik.submitForm();
      };

      useEffect(()=> {
        loadData();
      },[]);

    return (
        <DndProvider backend={HTML5Backend}>
        <Grid container>
            <Grid
                item
                xs={12}
                className="flex justify-end align-center gap-8"
                display='flex'
                justifyContent='space-between'
                marginBottom={"16px"}
                >
                <div className="flex-center mb-16 pl-20 mt-16">
                    <SettingsIcon /> 
                    <Typography variant="subtitle2">{t('Configuration')}</Typography>
                </div>
                <Button
                    variant={action !== GLOBAL_ACTION.NONE ? "outlined" : "contained"}
                    color="primary"
                    onClick={updateAction}
                >
                    {t(action !== GLOBAL_ACTION.NONE ? t("Close") : t("Add Config"))}
                </Button>
        </Grid>

        <Grid item xs={action !== GLOBAL_ACTION.NONE ? 6 : 12}>
          <Card className="h-full">
            <CardContent>
              <div className="action-header">
                <img src={deleteIcon} alt="Remove"  onClick={deleteSelected}/>
              </div>
                <DataGrid
                    totalPages={1}
                    columns={columns}
                    data={data || []}
                    selection={true}
                    selectionChanged={selectionChanged}
                />
              </CardContent>
              </Card>
              </Grid>

               {action !== GLOBAL_ACTION.NONE && (
                    <Grid item xs={6} paddingLeft={1}>
                            <Card className="h-full">
                                <CardContent>
                                <form>
                                    <div className="form-header">
                                        <Typography variant="subtitle1">{formik.values?.id ? t('Edit Config') : t('Create a New Config')}</Typography>
                                    </div>
                                    <div className="pt-32 pl-40 pr-40">
                                        <Box className="user-input">
                                          <FormControl sx={{ width: "100%" }}>
                                              <InputLabel htmlFor="configName"></InputLabel>
                                              <TextField
                                              type="text"
                                              id="configName"
                                              name="configName"
                                              disabled={formik.values?.fixedConfig}
                                              label={t("Configuration Name")}
                                              value={formik.values?.configName}
                                              onChange={formik.handleChange}
                                              error={formik.touched.configName && Boolean(formik.errors.configName)}
                                              helperText={formik.touched.configName && formik.errors.configName}
                                              />
                                          </FormControl>
                                         
                                        </Box>
                                        <Box className="user-input">
                                          <FormControl sx={{ width: "100%",mt:2 }}>
                                              <InputLabel htmlFor="configValue"></InputLabel>
                                              <TextField
                                              type="text"
                                              id="configValue"
                                              name="configValue"
                                              label={t("Configuration value")}
                                              value={formik.values?.configValue}
                                              onChange={formik.handleChange}
                                              error={formik.touched.configValue && Boolean(formik.errors.configValue)}
                                              helperText={formik.touched.configValue && formik.errors.configValue}
                                              />
                                          </FormControl>
                                        </Box>

                                        <Box className="user-input">
                                          <FormControl sx={{ width: "100%",mt:2 }}>
                                              <InputLabel htmlFor="description"></InputLabel>
                                              <TextField
                                              type="text"
                                              id="description"
                                              name="description"
                                              label={t("Description")}
                                              value={formik.values?.description}
                                              onChange={formik.handleChange}
                                              />
                                          </FormControl>
                                        </Box>
                                    </div>
                                    <div className="db center mt-54">
                                        <Button variant="contained" onClick={handleSave} color="primary">
                                        {t("Save")}
                                        </Button>
                                    </div>
                                    </form>
                                </CardContent>
                            </Card>
                    </Grid>
                    )}
        </Grid>
        </DndProvider>

      

    )
}